/**
 * +quests: the WoW-style quest window for side-quest NPCs (docs/design/zones.md "Side quests"):
 * a crest with the NPC's initials, name and title; the greeting typed out; the list of what they
 * have for you ("!" offers, "?" hand-ins, grey for in progress or not yet); an offer page (the
 * intro typed out, Objective, Rewards, Accept / Decline); a hand-in page (their punchline, the
 * rewards, Complete); a nag page for quests still in progress. Built on the town panel (one panel
 * at a time, Esc closes). DOM only: the world layer (world/sideQuests.ts) does the intents.
 */
import type { SideQuestDef } from '@keystone/content';
import { el } from '../dom';
import { openTownPanel, type TownPanel } from '../field/townPanel';
import type { QuestMenuEntry, QuestNames } from '../../world/sideQuestModel';
import { questProgressText } from '../../world/sideQuestModel';
import './quests.css';

export interface QuestDialogNpc {
  id: string;
  name: string;
  title?: string;
}

export interface QuestDialogDeps {
  host: HTMLElement;
  names: QuestNames;
  greeting: string;
  /** What they say once they have nothing more for you. */
  doneLine?: string;
  /** Fresh menu for this NPC (re-read after every action). */
  menu(): QuestMenuEntry[];
  /** Kills / items so far on an active quest. */
  progressOf(id: string): number;
  /** Take a quest: resolves true when it was accepted. */
  accept(id: string): Promise<boolean>;
  /** Hand a quest in: resolves true when it was paid. */
  turnIn(id: string): Promise<boolean>;
  onClose?(): void;
}

/** Characters per second of the typewriter. */
const TYPE_CPS = 75;
const reducedMotion = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A warm crest colour per NPC (stable per id). */
function crestColors(id: string): [string, string] {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return [`hsl(${hue} 80% 82%)`, `hsl(${hue} 55% 34%)`];
}
const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter((w) => /^[A-Z]/.test(w))
    .slice(-2)
    .map((w) => w[0])
    .join('');

/**
 * Type `lines` into `box` (one paragraph each). A click on the box finishes at once. Returns a
 * function that finishes it (also called when the panel closes).
 */
function typeLines(box: HTMLElement, lines: readonly string[], panel: TownPanel): () => void {
  box.replaceChildren();
  const ps = lines.map((t) => {
    const p = el('p', '', box);
    p.dataset['full'] = t;
    return p;
  });
  if (reducedMotion()) {
    ps.forEach((p) => (p.textContent = p.dataset['full'] ?? ''));
    return () => {};
  }
  const caret = el('span', 'caret');
  let i = 0;
  let c = 0;
  let last = performance.now();
  let raf = 0;
  const finish = (): void => {
    cancelAnimationFrame(raf);
    ps.forEach((p) => (p.textContent = p.dataset['full'] ?? ''));
    caret.remove();
  };
  const tick = (now: number): void => {
    if (panel.closed) return;
    c += ((now - last) / 1000) * TYPE_CPS;
    last = now;
    while (i < ps.length && c >= (ps[i]!.dataset['full'] ?? '').length) {
      ps[i]!.textContent = ps[i]!.dataset['full'] ?? '';
      c -= (ps[i]!.dataset['full'] ?? '').length;
      i++;
      // A short beat between lines.
      c -= 10;
    }
    if (i >= ps.length) return finish();
    const p = ps[i]!;
    p.textContent = (p.dataset['full'] ?? '').slice(0, Math.max(0, Math.floor(c)));
    p.appendChild(caret);
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  box.addEventListener('click', finish, { once: true });
  return finish;
}

function rewards(parent: HTMLElement, def: SideQuestDef, xp: number): void {
  el('div', 'fx-qd-h', parent, 'Rewards');
  const row = el('div', 'fx-qd-rewards', parent);
  const rw = (cls: string, icon: string, text: string, note?: string): void => {
    const b = el('div', `fx-qd-rw ${cls}`, row);
    el('i', '', b, icon);
    el('span', '', b, text);
    if (note) el('small', '', b, note);
  };
  rw('xp', 'XP', `${xp} XP`, xp < def.reward.xp ? '(outgrown)' : undefined);
  rw('gold', 'G', `${def.reward.gold} gold`);
  if (def.reward.gear) rw('gear', '+', 'A piece of gear');
}

function button(parent: HTMLElement, label: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', `fx-btn ${cls}`.trim(), parent, label);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

/** Open the quest window for `npc`. */
export function openQuestDialog(npc: QuestDialogNpc, deps: QuestDialogDeps): TownPanel {
  const panel = openTownPanel(deps.host, { title: npc.name, ...(npc.title ? { subtitle: npc.title } : {}), className: 'fx-qd', ...(deps.onClose ? { onClose: deps.onClose } : {}) });
  const head = panel.root.querySelector('.fx-panel-head');
  if (head) {
    const crest = el('div', 'fx-qd-crest');
    const [hi, lo] = crestColors(npc.id);
    crest.style.setProperty('--qd-hi', hi);
    crest.style.setProperty('--qd-lo', lo);
    el('span', '', crest, initials(npc.name) || npc.name.slice(0, 1));
    head.prepend(crest);
  }
  let stop: () => void = () => {};
  const page = (): { body: HTMLElement; foot: HTMLElement } => {
    stop();
    panel.body.replaceChildren();
    panel.foot.replaceChildren();
    return { body: el('div', 'fx-qd-body', panel.body), foot: panel.foot };
  };
  const say = (body: HTMLElement, lines: readonly string[]): void => {
    stop = typeLines(el('div', 'fx-qd-say', body), lines, panel);
  };
  const title = (body: HTMLElement, e: QuestMenuEntry): void => {
    const t = el('div', `fx-qd-qtitle${e.grey ? ' grey' : ''}`, body, e.def.title);
    el('small', '', t, `Level ${e.def.level}${e.grey ? ' · trivial' : ''}`);
  };

  const menuPage = (lead?: readonly string[]): void => {
    const entries = deps.menu();
    const { body, foot } = page();
    say(body, lead ?? [entries.length ? deps.greeting : (deps.doneLine ?? deps.greeting)]);
    if (entries.length) {
      const list = el('div', 'fx-qd-list', body);
      for (const e of entries) {
        const b = el('button', `fx-qd-item ${e.role}${e.grey ? ' grey' : ''}`, list);
        b.type = 'button';
        el('span', 'mk', b, e.role === 'turnin' ? '?' : e.role === 'progress' ? '?' : '!');
        el('span', '', b, e.def.title);
        el('small', '', b, e.role === 'locked' ? `Level ${e.def.minLevel}` : e.role === 'progress' ? 'In progress' : `Lv ${e.def.level}`);
        if (e.role === 'locked') b.disabled = true;
        else b.addEventListener('click', () => open(e));
      }
      const locked = entries.filter((e) => e.role === 'locked');
      if (locked.length && locked.length === entries.length) el('div', 'fx-qd-note', body, `Come back when you are level ${Math.min(...locked.map((e) => e.def.minLevel))}.`);
    }
    button(foot, 'Goodbye', 'ghost', () => panel.close());
  };

  const offerPage = (e: QuestMenuEntry): void => {
    const { body, foot } = page();
    title(body, e);
    say(body, e.def.intro);
    el('div', 'fx-qd-h', body, 'Objective');
    el('div', 'fx-qd-obj', body, e.def.objective);
    rewards(body, e.def, e.xp);
    const acc = button(foot, 'Accept', '', () => {
      acc.disabled = true;
      void deps.accept(e.def.id).then((ok) => {
        if (panel.closed) return;
        if (!ok) acc.disabled = false;
        else afterAction();
      });
    });
    button(foot, 'Decline', 'ghost', () => (deps.menu().length > 1 ? menuPage() : panel.close()));
  };

  const progressPage = (e: QuestMenuEntry): void => {
    const { body, foot } = page();
    title(body, e);
    say(body, [e.def.progress ?? 'How’s it going?']);
    el('div', 'fx-qd-h', body, 'Objective');
    const o = el('div', 'fx-qd-obj', body, `${e.def.objective} `);
    el('b', '', o, questProgressText(e.def, deps.progressOf(e.def.id), deps.names));
    rewards(body, e.def, e.xp);
    button(foot, 'Back', 'ghost', () => menuPage());
    button(foot, 'Goodbye', 'ghost', () => panel.close());
  };

  const turnInPage = (e: QuestMenuEntry): void => {
    const { body, foot } = page();
    title(body, e);
    say(body, e.def.turnIn);
    rewards(body, e.def, e.xp);
    const done = button(foot, 'Complete', '', () => {
      done.disabled = true;
      void deps.turnIn(e.def.id).then((ok) => {
        if (panel.closed) return;
        if (!ok) done.disabled = false;
        else afterAction(e.def.turnIn);
      });
    });
  };

  /** After an accept / hand-in: straight into the next offer (chains), else the menu or close. */
  const afterAction = (said?: readonly string[]): void => {
    const next = deps.menu();
    const offer = next.find((x) => x.role === 'turnin') ?? next.find((x) => x.role === 'offer');
    if (offer) return open(offer);
    if (said && next.length) return menuPage();
    panel.close();
  };

  const open = (e: QuestMenuEntry): void => {
    if (e.role === 'offer') offerPage(e);
    else if (e.role === 'turnin') turnInPage(e);
    else if (e.role === 'progress') progressPage(e);
  };

  // One thing to do (a hand-in or a single offer): straight to it, as in WoW.
  const first = deps.menu();
  const act = first.filter((x) => x.role === 'turnin' || x.role === 'offer');
  if (act.length === 1 && first.length === 1) open(act[0]!);
  else if (first.length === 1 && first[0]!.role === 'progress') open(first[0]!);
  else menuPage();
  const close = panel.close.bind(panel);
  panel.close = () => {
    stop();
    close();
  };
  return panel;
}
